import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  item, 
  type, 
  constraintError = null,
  loading = false 
}) => {
  const [showForceDelete, setShowForceDelete] = useState(false);

  if (!isOpen) return null;

  const getItemDisplayName = () => {
    switch (type) {
      case 'customers':
        return item?.customer_name || item?.email || 'customer';
      case 'invoices':
        return item?.invoice_number || 'invoice';
      case 'quotes':
        return item?.quote_number || 'quote';
      case 'payment-links':
        return item?.link_reference || 'payment link';
      case 'payments-received':
        return item?.payment_reference || 'payment';
      case 'delivery-challans':
        return item?.challan_number || 'delivery challan';
      case 'retainer-invoices':
        return item?.retainer_number || 'retainer invoice';
      case 'recurring-invoices':
        return item?.recurring_reference || 'recurring invoice';
      case 'credit-notes':
        return item?.credit_note_number || 'credit note';
      default:
        return 'item';
    }
  };

  const getItemType = () => {
    switch (type) {
      case 'payment-links': return 'payment link';
      case 'payments-received': return 'payment';
      case 'delivery-challans': return 'delivery challan';
      case 'retainer-invoices': return 'retainer invoice';
      case 'recurring-invoices': return 'recurring invoice';
      case 'credit-notes': return 'credit note';
      default: return type?.slice(0, -1) || 'item'; // Remove 's' from plural
    }
  };

  const handleConfirm = () => {
    if (constraintError && showForceDelete) {
      onConfirm(true); // Force delete
    } else {
      onConfirm(false); // Normal delete
    }
  };

  const handleClose = () => {
    setShowForceDelete(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Delete {getItemType()}
            </h3>
            <p className="text-sm text-gray-500">
              This action cannot be undone
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="mb-6">
          {!constraintError ? (
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-medium">{getItemDisplayName()}</span>? 
              This action cannot be undone.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Cannot Delete - Related Records Found
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>{constraintError?.message}</p>
                      {constraintError?.constraints && (
                        <p className="mt-1">
                          Related: {constraintError?.constraints?.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!showForceDelete ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    You have two options:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• Cancel and manually clean up related records first</li>
                    <li>• Force delete (this will also delete all related records)</li>
                  </ul>
                  
                  <Button
                    variant="outline"
                    className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={() => setShowForceDelete(true)}
                  >
                    Show Force Delete Option
                  </Button>
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        ⚠️ DANGER: Force Delete
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          This will permanently delete <span className="font-medium">{getItemDisplayName()}</span> and 
                          <span className="font-medium"> ALL related records</span> including:
                        </p>
                        <p className="mt-1">
                          {constraintError?.constraints?.join(', ')}
                        </p>
                        <p className="mt-2 font-medium">
                          This action is irreversible and may cause data loss!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          {(!constraintError || showForceDelete) && (
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={loading}
              className={showForceDelete ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Deleting...
                </div>
              ) : (
                showForceDelete ? 'Force Delete All' : 'Delete'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;